import { useMemo } from "react";
import type { Movie } from "@/mocks/movies";

export interface MovieQuery {
  keyword?: string;
  genre?: string;
  region?: string;
  year?: string;
  sort?: string;
}

function matchKeyword(movie: Movie, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;
  const haystack = [
    movie.title,
    movie.director,
    movie.excerpt,
    movie.synopsis,
    movie.tagline,
    movie.region,
    movie.year.toString(),
    ...movie.genres,
    ...movie.cast,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(kw);
}

export function applyQuery(movies: Movie[], query: MovieQuery): Movie[] {
  const { keyword, genre, region, year, sort } = query;

  let list = movies.filter((movie) => {
    if (keyword && !matchKeyword(movie, keyword)) return false;
    if (genre && genre !== "全部" && !movie.genres.includes(genre)) return false;
    if (region && region !== "全部" && movie.region !== region) return false;
    if (year && year !== "全部" && movie.year.toString() !== year) return false;
    return true;
  });

  list = [...list];

  if (sort === "year") {
    list.sort((a, b) => b.year - a.year || b.score - a.score);
  } else if (sort === "score") {
    list.sort((a, b) => b.score - a.score);
  } else {
    list.sort((a, b) => Number(b.featured) - Number(a.featured) || b.year - a.year);
  }

  return list;
}

export function useMovieQuery(movies: Movie[], query: MovieQuery): Movie[] {
  const { keyword, genre, region, year, sort } = query;
  return useMemo(
    () => applyQuery(movies, { keyword, genre, region, year, sort }),
    [movies, keyword, genre, region, year, sort],
  );
}